import type { ComponentType } from 'react'
import {
  Activity, Award, Clapperboard, Mic, Badge, Bell, Book, BookOpen, Box, Briefcase, Calendar, CalendarCheck, CalendarClock,
  CheckCircle, ClipboardCheck, ClipboardList, ClipboardPen, Code, Shapes, Compass, Cpu, CreditCard, Crown, Diamond, DoorOpen, DollarSign, Download,
  Clock, ExternalLink, FileText, Flame, Folder, Gem, Gift, Globe, GraduationCap, Hammer, Handshake, Hash, Heart, Hexagon, Home,
  Image, Key, LifeBuoy, Lightbulb, Lock, Mail, MapPin, Medal, Megaphone, MessageCircle, MessageSquare, MessagesSquare,
  Music, Navigation, Newspaper, Package, Palette, Phone, Puzzle, Rocket, Settings, Shield, ShoppingBag,
  ShoppingCart, Sparkles, Star, Tag, Target, ThumbsUp, TrendingUp, Trophy, Unlock, User, UserCheck, Users, Video,
  Wrench, Zap,
} from 'lucide-react'

export type IconComponent = ComponentType<{ className?: string }>

/**
 * Every icon a space, section, plan group or module can wear, by the kebab-case
 * name that is stored in the database.
 *
 * Lucide used to be imported whole (`import * as LucideIcons`) to resolve any
 * stored name, which put all ~1,500 icons into the entry chunk. Names are now
 * looked up here; one that is not listed falls back to the caller's default,
 * as an unknown name always did. A new icon in a picker must be added here.
 */
const ICONS: Record<string, IconComponent> = {
  home: Home, folder: Folder, star: Star, heart: Heart, users: Users, user: User,
  calendar: Calendar, 'book-open': BookOpen, 'graduation-cap': GraduationCap, trophy: Trophy,
  award: Award, medal: Medal, image: Image, video: Video, music: Music, 'file-text': FileText,
  newspaper: Newspaper, book: Book, megaphone: Megaphone, activity: Activity, 'trending-up': TrendingUp,
  zap: Zap, flame: Flame, rocket: Rocket, briefcase: Briefcase, 'shopping-bag': ShoppingBag,
  'shopping-cart': ShoppingCart, 'credit-card': CreditCard, 'dollar-sign': DollarSign,
  'message-square': MessageSquare, 'message-circle': MessageCircle, mail: Mail, bell: Bell, phone: Phone,
  'map-pin': MapPin, globe: Globe, compass: Compass, navigation: Navigation, target: Target,
  settings: Settings, hammer: Hammer, wrench: Wrench, code: Code, cpu: Cpu, lightbulb: Lightbulb,
  sparkles: Sparkles, palette: Palette, puzzle: Puzzle, gift: Gift, shield: Shield, lock: Lock,
  key: Key, crown: Crown, gem: Gem,
  /* Module defaults. */
  'external-link': ExternalLink, 'calendar-check': CalendarCheck, 'calendar-clock': CalendarClock,
  'door-open': DoorOpen, download: Download, 'life-buoy': LifeBuoy, package: Package, tag: Tag,
  'user-check': UserCheck, 'clipboard-list': ClipboardList, 'clipboard-pen': ClipboardPen, 'clipboard-check': ClipboardCheck, shapes: Shapes,
  mic: Mic, clapperboard: Clapperboard, 'thumbs-up': ThumbsUp, handshake: Handshake, hash: Hash, clock: Clock, 'messages-square': MessagesSquare,
  /* Plan group icons. */
  unlock: Unlock, 'check-circle': CheckCircle, badge: Badge, box: Box,
  diamond: Diamond, hexagon: Hexagon,
}

/** Names that were offered once but lucide never had (APP-70): `tool` showed the home icon. */
const ICON_ALIASES: Record<string, string> = { tool: 'hammer' }

/** The icon stored under `name` (kebab-case), or `fallback` when there is none or it is unknown. */
export const resolveIcon = <F extends IconComponent>(name: string | null | undefined, fallback: F): IconComponent | F => {
  if (!name) return fallback
  return ICONS[ICON_ALIASES[name] || name] || fallback
}
